/**
 * Preview Transactions Helper
 *
 * Fetches and filters the external CSV transaction file for a single shareholder,
 * returning the rows as a per-share-class map. Used by the `/admin/profile`
 * shareholder preview path so templates can render the correct per-class basis
 * for EXTERNAL_DATA clients (e.g. PharmaJet) — where basis is derived from
 * transactions, not Contribution events.
 *
 * Design constraints (to avoid the #5076 revert's side effects):
 * - NEVER mutates clientData, userData, sessionStorage, or any global state.
 * - NEVER dispatches events.
 * - NEVER touches the dashboard's own transaction load path.
 * - Admin-preview-only — only callers on /admin/profile should use this.
 *
 * #6279: row parsing now goes through transaction-schema.js's buildHeaderMap()/
 * fromCSVRow() -- the same canonical, header-alias-based field resolution the
 * backend (equity-transactions-scoped.py) uses -- instead of a hand-rolled set
 * of fixed column indices. The old indices had drifted from the live CSV shape
 * (the doc comment they were meant to mirror, `loadTheseTransactions` in
 * index.js, no longer exists there), so a client whose actual columns didn't
 * match those positions got the wrong field read into taxBasis/sharePrice/etc.
 * Falls back to fromCSVRow's own fixed-column parsing only for a genuinely
 * headerless CSV.
 */

import { cleanCSV } from './string-utils.js';
import { buildHeaderMap, fromCSVRow } from './transaction-schema.js';

/**
 * In-memory cache keyed by CSV filename. Scoped to the browser tab's session.
 * Cleared by page reload, which is acceptable for admin tooling.
 */
const csvRowCache = new Map();

/**
 * Fetch the external-data CSV (once per session per file) and filter it for a
 * single shareholder's transactions.
 *
 * @param {Object} options
 * @param {Object} options.clientData - Full client configuration (reads `.transactions_data`)
 * @param {string} options.userid - The previewed shareholder's userid (email)
 * @param {Object} [options.clientFlags] - { isPHJ, isACR } for column-specific handling. Defaults to window.* flags.
 * @param {boolean} [options.useTaxBasis] - Same meaning as `USE_TAX_BASIS` in index.js. Defaults to window.USE_TAX_BASIS.
 * @param {Function} [options.getContentUrlFn] - Injected for tests. Defaults to window.getContentUrl.
 * @param {Function} [options.fetchFn] - Injected for tests. Defaults to globalThis.fetch.
 * @returns {Promise<Object|null>} Map of { [shareClass]: Transaction[] } or null if client has no transactions_data.
 */
export async function fetchTransactionsForPreview({ clientData, userid, clientFlags = null, useTaxBasis = null, getContentUrlFn = null, fetchFn = null }) {
	if (!clientData?.transactions_data || !userid) return null;

	const flags = clientFlags || {
		isPHJ: typeof window !== 'undefined' && !!window.isPHJ,
		isACR: typeof window !== 'undefined' && !!window.isACR,
	};
	const useTB = useTaxBasis !== null ? useTaxBasis : typeof window !== 'undefined' && !!window.USE_TAX_BASIS;
	const getUrl = getContentUrlFn || (typeof window !== 'undefined' ? window.getContentUrl : null);
	const doFetch = fetchFn || (typeof globalThis !== 'undefined' ? globalThis.fetch : null);

	if (!getUrl || !doFetch) {
		console.warn('[preview-transactions] Missing getContentUrl or fetch — cannot load CSV');
		return null;
	}

	const csvKey = clientData.transactions_data;
	let rows = csvRowCache.get(csvKey);
	if (!rows) {
		const url = await getUrl(csvKey);
		const response = await doFetch(url);
		const text = await response.text();
		rows = cleanCSV(text);
		csvRowCache.set(csvKey, rows);
	}

	const headerMap = buildHeaderMap(rows[0]);
	const dataRows = headerMap ? rows.slice(1) : rows;

	const me = userid.toLowerCase();
	const result = {};
	const seenNridsByClass = {};

	dataRows.forEach((row) => {
		const txn = fromCSVRow(row, { headerMap, isFund: flags.isACR });
		if (!txn) return; // zero/empty quantity

		if (!txn.shareClass || txn.shareClass.endsWith('-Debt')) return;
		if (txn.sender !== me && txn.receiver !== me) return;

		// Direction comes from sender/receiver, not sign -- calculator-input-builder's
		// computeNetTransfers() expects a positive magnitude (mirrors this helper's
		// pre-#6279 unsigned-quantity parsing).
		const quantity = Math.abs(Number(txn.quantity)) || 0;
		if (!quantity) return;
		txn.quantity = quantity.toString();

		if (!seenNridsByClass[txn.shareClass]) seenNridsByClass[txn.shareClass] = new Set();
		if (seenNridsByClass[txn.shareClass].has(txn.nrid)) return;
		seenNridsByClass[txn.shareClass].add(txn.nrid);

		if (!flags.isPHJ && !useTB) {
			delete txn.strikePrice;
			delete txn.expDate;
		}

		if (!result[txn.shareClass]) result[txn.shareClass] = [];
		result[txn.shareClass].push(txn);
	});

	return result;
}

/**
 * Clear the cached CSV rows. Call between tests or when the admin switches clients.
 */
export function clearPreviewTransactionsCache() {
	csvRowCache.clear();
}
